import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard, Calendar, DollarSign, ArrowUpRight,
  AlertCircle, CheckCircle, XCircle, Clock,
  Stethoscope, Users, FileText, RefreshCw, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserEntitlements, Entitlement, ProductId } from '../../services/entitlements';
import { callGetPaymentHistory, callGetSubscriptionDetails, PaymentRecord, SubscriptionInfo } from '../../services/billing';

const PRODUCT_META: Record<ProductId, { name: string; icon: React.ComponentType<{ className?: string }> }> = {
  vetaxis: { name: 'VetAxis', icon: Stethoscope },
  staffaxis: { name: 'StaffAxis', icon: Users },
  noteaxis: { name: 'NoteAxis', icon: FileText },
};

const statusColors: Record<string, string> = {
  active: 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30',
  trialing: 'text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30',
  past_due: 'text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/30',
  canceled: 'text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30',
  none: 'text-slate-600 dark:text-gray-400 bg-slate-50 dark:bg-gray-800/20 border-slate-200 dark:border-gray-700',
};

const paymentStatusIcon: Record<string, React.ReactNode> = {
  COMPLETE: <CheckCircle className="w-4 h-4 text-green-500" />,
  ACTIVE: <CheckCircle className="w-4 h-4 text-green-500" />,
  CANCELLED: <XCircle className="w-4 h-4 text-red-500" />,
  FAILED: <XCircle className="w-4 h-4 text-red-500" />,
};

type SubDetail = { productId: ProductId; entitlement: Entitlement; subscription: SubscriptionInfo | null; loading: boolean };

const SubscriptionCard: React.FC<{ detail: SubDetail; onRefresh: (pid: ProductId) => void }> = ({ detail, onRefresh }) => {
  const { productId, entitlement, subscription, loading } = detail;
  const meta = PRODUCT_META[productId];
  const Icon = meta.icon;
  const isActive = entitlement.status === 'active' || entitlement.status === 'trialing';
  const nextBilling = subscription?.payfast?.nextBillingDate || subscription?.payfastLive?.run_date || null;
  const amount = subscription?.payfast?.amount || (subscription?.payfastLive?.amount ? `${subscription.payfastLive.amount}` : null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
            <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">{meta.name}</h3>
            <span className="text-sm text-slate-500 dark:text-gray-400 capitalize">{entitlement.plan} plan</span>
          </div>
        </div>
        <button
          onClick={() => onRefresh(productId)}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          title="Refresh subscription details"
        >
          <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600 dark:text-gray-400">Status</span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusColors[entitlement.status] || statusColors.none}`}>
            {entitlement.status === 'none' ? 'No Subscription' : entitlement.status.replace('_', ' ')}
          </span>
        </div>
        {amount && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-gray-400 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Amount
            </span>
            <span className="text-sm font-medium text-slate-900 dark:text-white">R{amount}/month</span>
          </div>
        )}
        {nextBilling && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600 dark:text-gray-400 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Next billing
            </span>
            <span className="text-sm font-medium text-slate-900 dark:text-white">{nextBilling}</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-3 border-t border-slate-200 dark:border-gray-700">
        {isActive ? (
          <motion.a
            href="/plans"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 text-center py-2 px-3 rounded-xl text-sm font-medium border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
          >
            Change Plan
          </motion.a>
        ) : (
          <motion.a
            href="/plans"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex-1 text-center py-2 px-3 rounded-xl text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Subscribe
          </motion.a>
        )}
      </div>
    </motion.div>
  );
};

const Billing: React.FC = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<SubDetail[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const load = async () => {
      try {
        // Fetch entitlements
        const ents = await fetchUserEntitlements(user.uid);
        const details: SubDetail[] = (['vetaxis', 'staffaxis', 'noteaxis'] as ProductId[]).map(pid => ({
          productId: pid,
          entitlement: ents[pid],
          subscription: null,
          loading: false,
        }));
        if (mounted) setSubscriptions(details);

        // Fetch subscription details for active products
        for (const d of details) {
          if (d.entitlement.status !== 'none') {
            if (mounted) setSubscriptions(prev => prev.map(s => s.productId === d.productId ? { ...s, loading: true } : s));
            try {
              const { subscription } = await callGetSubscriptionDetails({ productId: d.productId });
              if (mounted) {
                setSubscriptions(prev => prev.map(s =>
                  s.productId === d.productId ? { ...s, subscription, loading: false } : s
                ));
              }
            } catch {
              if (mounted) {
                setSubscriptions(prev => prev.map(s =>
                  s.productId === d.productId ? { ...s, loading: false } : s
                ));
              }
            }
          }
        }

        // Fetch payment history
        const { payments: paymentList } = await callGetPaymentHistory({ limit: 25 });
        if (mounted) setPayments(paymentList);
      } catch (err: any) {
        if (mounted) setError(err?.message || 'Failed to load billing data');
      } finally {
        if (mounted) setLoadingPayments(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [user]);

  const refreshSubscription = async (productId: ProductId) => {
    setSubscriptions(prev => prev.map(s => s.productId === productId ? { ...s, loading: true } : s));
    try {
      const { subscription } = await callGetSubscriptionDetails({ productId });
      setSubscriptions(prev => prev.map(s =>
        s.productId === productId ? { ...s, subscription, loading: false } : s
      ));
    } catch {
      setSubscriptions(prev => prev.map(s =>
        s.productId === productId ? { ...s, loading: false } : s
      ));
    }
  };

  const formatDate = (ts: any) => {
    if (!ts) return '—';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loadingPayments && subscriptions.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <motion.div
          className="glass-effect rounded-2xl p-8 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-600 dark:text-gray-300">Loading billing information...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            <span className="gradient-text">Billing</span> & Subscriptions
          </h1>
          <p className="text-lg text-slate-600 dark:text-gray-300 max-w-2xl mx-auto">
            Manage your subscriptions and view payment history
          </p>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex items-center gap-3 max-w-4xl mx-auto"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          </motion.div>
        )}

        {/* Subscription Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {subscriptions.map(detail => (
            <SubscriptionCard
              key={detail.productId}
              detail={detail}
              onRefresh={refreshSubscription}
            />
          ))}
        </motion.div>

        {/* Payment History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="glass-effect rounded-2xl shadow-glass overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary-500/10 dark:bg-primary-400/10">
                  <CreditCard className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Payment History</h2>
                  <p className="text-sm text-slate-500 dark:text-gray-400">Your recent transactions</p>
                </div>
              </div>
            </div>

            {payments.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-gray-400 mb-2">No payments yet</p>
                <p className="text-sm text-slate-500 dark:text-gray-500">
                  Your payment history will appear here once you subscribe to a plan.
                </p>
                <motion.a
                  href="/plans"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-2 mt-4 btn-primary text-sm"
                >
                  <ArrowUpRight className="w-4 h-4" />
                  View Plans
                </motion.a>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-800/30 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 whitespace-nowrap">
                          {formatDate(payment.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 capitalize whitespace-nowrap">
                          {payment.productId}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-gray-300 capitalize whitespace-nowrap">
                          {payment.planId}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900 dark:text-white whitespace-nowrap">
                          {payment.amountGross ? `R${payment.amountGross}` : '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                            {paymentStatusIcon[payment.paymentStatus?.toUpperCase()] || <Clock className="w-4 h-4 text-slate-400" />}
                            <span className="capitalize">{(payment.paymentStatus || 'pending').toLowerCase()}</span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>

        {/* Help Section */}
        <motion.div
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Need help with billing?{' '}
            <a href="/support" className="text-primary-600 dark:text-primary-400 hover:underline">Contact Support</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Billing;
